import { getDashboard } from "@/api/financialReport";
import { consoleHandlerError, extractErrorMessage } from "@/utils/message";
import { createTakeLatest } from "@/utils/takeLatest";
import { formatPeriodLabel } from "./utils/dateRange";

// Tab Dashboard del reporte (docs/decisiones/37-dashboard-reporte-financiero.md): la
// tabla resumen del mes focal y las series de los 4 gráficos llegan en UNA
// respuesta de `/financial-reports/dashboard/`. Módulo propio a propósito: el
// de Resultados (`financialReports`) es compartido con Rentabilidad y pedir
// otra ventana ahí pisaría lo que esos tabs muestran.
//
// Los % (filas de ratio, márgenes, Margen EBITDA) viajan como fracción
// (0.86 = 86 %), igual que los deltas; el formateo vive en la vista.

export const DASHBOARD_MODE_RESULTADOS = "resultados";
export const DASHBOARD_ERROR_MESSAGE = "No pudimos cargar el dashboard. Vuelve a intentarlo en unos segundos.";

// Take-latest: cambiar de mes más rápido de lo que responde el backend no deja
// que una respuesta vieja pise la del mes elegido.
const runLatest = createTakeLatest();

const toNumberOrNull = (value) => (value == null ? null : Number(value));
const toSeries = (values) => (values || []).map(toNumberOrNull);

export function normalizeDashboard(data) {
    const months = data.series?.months || [];
    return {
        month: data.month,
        floorMonth: data.floor_month,
        lastDataMonth: data.last_data_month,
        rows: (data.rows || []).map((row) => ({
            key: row.key,
            label: row.label,
            isMoney: row.is_money,
            emphasis: row.emphasis,
            theme: row.theme,
            polarity: row.polarity,
            targetTab: row.target_tab,
            // Cuentas plegables (2026-10-07): la hija apunta a su total.
            parentKey: row.parent_key || null,
            hasChilds: Boolean(row.has_childs),
            // Cuenta del plan para acotar el drawer: nivel 2 (línea) o 3
            // (cuenta bajo la línea, con la línea como grupo).
            account: row.account || null,
            accountLevel: row.account_level || null,
            accountGroup: row.account_group || null,
            values: {
                month: toNumberOrNull(row.values.month),
                prevMonth: toNumberOrNull(row.values.prev_month),
                prevYearMonth: toNumberOrNull(row.values.prev_year_month),
                ytd: toNumberOrNull(row.values.ytd),
                prevYtd: toNumberOrNull(row.values.prev_ytd),
            },
            deltas: {
                mom: toNumberOrNull(row.deltas.mom),
                yoy: toNumberOrNull(row.deltas.yoy),
                ytd: toNumberOrNull(row.deltas.ytd),
            },
        })),
        series: {
            months,
            monthLabels: months.map((month) => formatPeriodLabel(month, "month")),
            sales: { current: toSeries(data.series?.sales?.current) },
            burn: (data.series?.burn || []).map((serie) => ({ ...serie, data: toSeries(serie.data) })),
            margins: (data.series?.margins || []).map((serie) => ({ ...serie, data: toSeries(serie.data) })),
            ebitda: { amount: toSeries(data.series?.ebitda?.amount) },
        },
    };
}

const state = () => ({
    mode: DASHBOARD_MODE_RESULTADOS,
    // El payload anterior se conserva mientras carga el mes nuevo: la vista
    // lo atenúa en vez de vaciarse.
    dashboard: null,
    isLoading: false,
    error: null,
});

const getters = {
    hasDashboard: (state) => state.dashboard !== null,
};

const mutations = {
    SET_DASHBOARD(state, dashboard) {
        state.dashboard = dashboard;
    },
    SET_LOADING(state, isLoading) {
        state.isLoading = isLoading;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
};

const actions = {
    // `initDate` / `endDate`: el rango del tab en YYYY-MM-DD (el que resuelve
    // `resolveDateRange` con los filtros compartidos). La tabla es la del
    // último mes del rango; las series cubren sus meses.
    async fetchDashboard({ commit, state }, { initDate, endDate } = {}) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        let stale = false;
        try {
            const result = await runLatest((signal) =>
                getDashboard({ initDate, endDate }, state.mode, { signal }),
            );
            stale = result.stale;
            if (stale) return;
            commit("SET_DASHBOARD", normalizeDashboard(result.value.data));
        } catch (error) {
            consoleHandlerError({ error, prefix: "[dashboardReport]", silent: true });
            commit("SET_ERROR", extractErrorMessage(error, DASHBOARD_ERROR_MESSAGE));
        } finally {
            if (!stale) commit("SET_LOADING", false);
        }
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
